/**
 * Admin page for the issues raised in results' reviews (specification §10): each open issue with
 * its campaign and item, and a decision. The decision is published with the issue in the next
 * revision of the result. Upholding an issue may correct a check of its item, which the revision
 * then carries, if it is published by Day 8.
 */

import { itemChecksOf, type Platform } from "@morcast/campaigns";
import { CircleCheck } from "lucide-react";
import { useId, useState } from "react";
import { Link } from "react-router";

import {
  type AdminIssue,
  CORRECTABLE_CHECKS,
  type CorrectableCheck,
  useAdminIssues,
  useDecideIssue,
} from "../../campaigns/issues.js";
import { AddressLink } from "../../components/ChainLinks.js";
import { Checkbox, Radio, Select } from "../../components/forms.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import {
  EmptyState,
  ErrorMessage,
  Fact,
  Loading,
  Notice,
  PageHeader,
  styles,
} from "../../components/ui.js";
import { useAppConfig } from "../../config.js";
import { formatUtc } from "../../format.js";

/** The checks an upheld issue can correct, each with the reasons it gives when it fails. */
const CHECK_OPTIONS = Object.entries(CORRECTABLE_CHECKS).map(([value, label]) => {
  const [, name = label, reasons] = /^(.+?) \((.+)\)$/.exec(label) ?? [];
  return { value: value as CorrectableCheck, label: name, ...(reasons ? { hint: reasons } : {}) };
});

/** The checks the items of a platform pass, which are the only ones a correction can change. */
function checkOptionsFor(platform: Platform) {
  const checks: readonly string[] = itemChecksOf(platform);
  return CHECK_OPTIONS.filter((option) => checks.includes(option.value));
}

/** What a corrected check becomes. */
const CHECK_OUTCOMES = [
  { value: "fail", label: "Fail" },
  { value: "pass", label: "Pass" },
] as const;

export function AdminIssues() {
  return (
    <RequireAdmin>
      <IssuesPage />
    </RequireAdmin>
  );
}

function IssuesPage() {
  const issues = useAdminIssues("open");
  return (
    <section className="max-w-5xl">
      <PageHeader eyebrow="Admin" title="Issues" documentTitle="Issues · Admin">
        Issues the brands and creators raised in results' reviews, oldest first. A decision made by
        Day 8 is published with its issue in the next revision of the result, so write it for them.
        An open issue holds the campaign's settlement until it is decided.
      </PageHeader>
      <div className="mt-8">
        {issues.isPending && <Loading />}
        {issues.isError && <ErrorMessage error={issues.error} />}
        {issues.data?.length === 0 && <EmptyState icon={CircleCheck} title="No issue is open." />}
        {issues.data && issues.data.length > 0 && (
          <ul className="space-y-4">
            {issues.data.map((issue) => (
              <IssueCard key={issue.id} issue={issue} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function IssueCard({ issue }: { issue: AdminIssue }) {
  const { chainId } = useAppConfig();
  const decide = useDecideIssue();
  const [outcome, setOutcome] = useState<"upheld" | "rejected">("rejected");
  const [decision, setDecision] = useState("");
  const { campaign, item } = issue;
  const checkOptions = checkOptionsFor(campaign.platform);
  const [correct, setCorrect] = useState(false);
  const [check, setCheck] = useState<CorrectableCheck>(checkOptions[0]?.value ?? "edits");
  const [checkOutcome, setCheckOutcome] = useState<"pass" | "fail">("fail");
  const fieldId = useId();
  // Past Day 8 no revision is published. A decision only ends the issue.
  const revisionsClosed = Date.now() > Date.parse(campaign.lastRevisionBy);
  const correcting = outcome === "upheld" && correct && item !== null && !revisionsClosed;

  function send() {
    decide.mutate({
      id: issue.id,
      outcome,
      decision: decision.trim(),
      ...(correcting && item
        ? { correction: { submissionId: item.submissionId, check, outcome: checkOutcome } }
        : {}),
    });
  }

  return (
    <li className={styles.card}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">
          <Link to={`/campaigns/${campaign.escrow}/${campaign.id}`} className="hover:underline">
            {campaign.title}
          </Link>
        </h2>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          Raised {formatUtc(issue.raisedAt)}
        </span>
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <Fact label="Raised by">
          <AddressLink chainId={chainId} address={issue.raisedBy} short />{" "}
          {issue.raisedBy === campaign.brand ? "(brand)" : "(creator)"}
        </Fact>
        <Fact label="About">
          {item ? (
            <a href={item.url} className="break-all hover:underline">
              {item.url}
            </a>
          ) : (
            "The whole campaign"
          )}
        </Fact>
        <Fact label="Corrections by">{formatUtc(campaign.lastRevisionBy)}</Fact>
      </dl>
      <p className="mt-4 text-sm whitespace-pre-line">{issue.summary}</p>
      {revisionsClosed && (
        <Notice tone="warning" className="mt-4">
          The last revision was due by {formatUtc(campaign.lastRevisionBy)}. Deciding the issue now
          lets the settlement go ahead, but no revision publishes the decision or corrects a check.
        </Notice>
      )}

      <fieldset className="mt-4 flex gap-6 text-sm">
        <legend className="sr-only">Outcome</legend>
        {(["upheld", "rejected"] as const).map((value) => (
          <label key={value} className="flex cursor-pointer items-center gap-2">
            <Radio
              name={`outcome-${issue.id}`}
              checked={outcome === value}
              onChange={() => setOutcome(value)}
            />
            {value === "upheld" ? "Uphold" : "Reject"}
          </label>
        ))}
      </fieldset>
      {outcome === "upheld" && item && !revisionsClosed && (
        <div className="mt-3 space-y-3 text-sm">
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox checked={correct} onChange={(event) => setCorrect(event.target.checked)} />
            Correct a check of this item
          </label>
          {correct && (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem]">
              <div>
                <label
                  id={`${fieldId}-check-label`}
                  htmlFor={`${fieldId}-check`}
                  className="font-medium"
                >
                  Check
                </label>
                <Select
                  id={`${fieldId}-check`}
                  labelId={`${fieldId}-check-label`}
                  value={check}
                  options={checkOptions}
                  onChange={setCheck}
                />
              </div>
              <div>
                <label
                  id={`${fieldId}-outcome-label`}
                  htmlFor={`${fieldId}-outcome`}
                  className="font-medium"
                >
                  New outcome
                </label>
                <Select
                  id={`${fieldId}-outcome`}
                  labelId={`${fieldId}-outcome-label`}
                  value={checkOutcome}
                  options={CHECK_OUTCOMES}
                  onChange={setCheckOutcome}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <label className="mt-3 block text-sm font-medium">
        Decision, as published
        <textarea
          rows={2}
          value={decision}
          onChange={(event) => setDecision(event.target.value)}
          className={styles.input}
        />
      </label>
      <button
        type="button"
        disabled={decide.isPending || decision.trim().length < 5}
        onClick={send}
        className={`mt-3 ${styles.primaryButton}`}
      >
        Decide
      </button>
      {decide.isError && <ErrorMessage error={decide.error} />}
    </li>
  );
}
